/**
 * DisclosuresAdmin.tsx — Admin > Forms/Templates > Disclosures.
 *
 * Legacy's Disclosures (admin/admin_disclosures_list.php, _details.php): a
 * named disclosure made of text blocks, printed on receipts, print templates
 * and payment links. In the Lead Sources layout (see components/admin/AdminList.tsx):
 *   - Name and Text Blocks (how many), sorted by name; search by name or text;
 *     Show deleted;
 *   - "Add disclosure" top right; a click on a live row opens it; Edit / Delete
 *     on a live row, Restore on a deleted one;
 *   - legacy's form: Name * and the text blocks ("Add Text Block"), each one
 *     movable up or down and removable; blank blocks are not saved.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why
 * (R8, R18): no Add, row icons or Actions column; a disclosure still opens as
 * a viewer, its blocks read-only, with no move / remove / add.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { getErrorMessage } from '@/services/apiClient';
import {
  DISCLOSURE_NAME_MAX,
  createDisclosure,
  deleteDisclosure,
  listDisclosures,
  restoreDisclosure,
  updateDisclosure,
  type AdminDisclosure,
} from '@/services/disclosuresAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'blocks', header: 'Text Blocks', sortable: true, className: 'w-32' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function disclosureName(d: AdminDisclosure): string {
  return d.name.trim() || `Disclosure #${d.id}`;
}

const SORT_COMPARE: ListSortCompare<AdminDisclosure> = {
  name: (a, b) => compareText(disclosureName(a), disclosureName(b)),
  blocks: (a, b) => a.sections.length - b.sections.length,
};

export function DisclosuresAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [disclosures, setDisclosures] = useState<AdminDisclosure[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminDisclosure | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminDisclosure | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setDisclosures(await listDisclosures());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load disclosures'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      disclosures.filter(
        (d) =>
          (showDeleted || d.active) &&
          (query === '' || d.name.toLowerCase().includes(query) || d.sections.some((s) => s.toLowerCase().includes(query))),
      ),
    [disclosures, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { id } = deleting;
    try {
      await deleteDisclosure(id);
      setDisclosures((prev) => prev.map((d) => (d.id === id ? { ...d, active: false } : d)));
      toast.success('Disclosure deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The disclosure could not be deleted.'));
    }
  }

  async function handleRestore(d: AdminDisclosure) {
    try {
      await restoreDisclosure(d.id);
      setDisclosures((prev) => prev.map((x) => (x.id === d.id ? { ...x, active: true } : x)));
      toast.success('Disclosure restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The disclosure could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Disclosures"
      subtitle="Disclosure text printed on receipts, print templates and payment links."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add disclosure
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search disclosures..."
          searchLabel="Search disclosures"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="disclosures-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading disclosures…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No disclosures found' : 'No disclosures yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a disclosure to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Disclosures" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((d) => {
              const deleted = !d.active;
              const name = disclosureName(d);
              return (
                <tr key={d.id} className={adminRowClass({ deleted, clickable: !deleted })} onClick={deleted ? undefined : () => setEditing(d)}>
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="disclosure-name">
                      {name}
                    </AdminRowName>
                    {d.sections[0] && <div className="mt-0.5 max-w-xl truncate text-xs text-slate-400">{d.sections[0]}</div>}
                  </td>
                  <td className={ADMIN_TD_CLASS}>{d.sections.length}</td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(d)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(d)} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(d)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['disclosure', 'disclosures']} />
        </>
      )}

      {editing && (
        <DisclosureFormModal
          key={editing === 'new' ? 'new' : editing.id}
          disclosure={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${disclosureName(deleting)}"?` : ''}
        message="It will no longer be offered for receipts and print templates. You can restore it from Show deleted."
        confirmLabel="Delete disclosure"
        variant="danger"
      />
    </AdminListPage>
  );
}

interface Block {
  key: number;
  text: string;
}

let nextBlockKey = 0;
const toBlocks = (texts: string[]): Block[] => texts.map((text) => ({ key: nextBlockKey++, text }));

function DisclosureFormModal({
  disclosure,
  onClose,
  onSaved,
}: {
  disclosure: AdminDisclosure | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = disclosure === null;
  // Locked: a viewer — the blocks only read, with no move, remove or add.
  const locked = useAdminPageLevel() === 'locked';
  const [name, setName] = useState(disclosure?.name ?? '');
  // A new disclosure starts with one empty block, as legacy's page did.
  const [initial] = useState<Block[]>(() => toBlocks(disclosure ? disclosure.sections : ['']));
  const [blocks, setBlocks] = useState<Block[]>(initial);
  const [saving, setSaving] = useState(false);

  const texts = blocks.map((b) => b.text);
  const dirty = name !== (disclosure?.name ?? '') || JSON.stringify(texts) !== JSON.stringify(initial.map((b) => b.text));
  const missingName = name.trim() === '';
  const hasText = texts.some((t) => t.trim() !== '');

  function move(index: number, by: number) {
    setBlocks((prev) => {
      const next = [...prev];
      const [b] = next.splice(index, 1);
      next.splice(index + by, 0, b);
      return next;
    });
  }

  async function save() {
    if (missingName) return;
    setSaving(true);
    try {
      if (disclosure) await updateDisclosure(disclosure.id, name, texts);
      else await createDisclosure(name, texts);
      toast.success(isNew ? 'Disclosure created' : 'Disclosure updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The disclosure could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Disclosure' : 'Edit Disclosure'}
      description={locked ? 'Text blocks print in this order.' : 'Text blocks print in this order. Blank blocks are not saved.'}
      isNew={isNew}
      dirty={isNew ? !missingName || hasText : dirty}
      saving={saving}
      errors={missingName ? ['Name is required'] : []}
      suggestions={hasText ? [] : ['A disclosure with no text prints nothing']}
      onSave={() => void save()}
      onRevertAll={() => {
        setName(disclosure?.name ?? '');
        setBlocks(initial);
      }}
      onClose={onClose}
      size="xl"
    >
      <div className="grid gap-4">
        <DialogField
          label="Name"
          htmlFor="disclosure-name"
          required
          state={missingName ? 'error' : 'none'}
          labelHint={missingName ? '— required' : undefined}
        >
          <Input
            id="disclosure-name"
            value={name}
            maxLength={DISCLOSURE_NAME_MAX}
            placeholder="e.g. Financing terms"
            onChange={(e) => setName(e.target.value)}
            className={fieldClasses.input(missingName ? 'error' : 'none')}
          />
        </DialogField>

        {blocks.map((b, i) => (
          <DialogField key={b.key} label={`Text block ${i + 1}`} htmlFor={`disclosure-block-${b.key}`}>
            <div className="flex items-start gap-2">
              <Textarea
                id={`disclosure-block-${b.key}`}
                rows={5}
                value={b.text}
                onChange={(e) => setBlocks((prev) => prev.map((x) => (x.key === b.key ? { ...x, text: e.target.value } : x)))}
                className="flex-1"
              />
              {!locked && (
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    leftIcon={<ArrowUp className="h-3.5 w-3.5" />}
                    aria-label={`Move text block ${i + 1} up`}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  />
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    leftIcon={<ArrowDown className="h-3.5 w-3.5" />}
                    aria-label={`Move text block ${i + 1} down`}
                    disabled={i === blocks.length - 1}
                    onClick={() => move(i, 1)}
                  />
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    leftIcon={<Trash2 className="h-3.5 w-3.5 text-red-600" />}
                    aria-label={`Remove text block ${i + 1}`}
                    onClick={() => setBlocks((prev) => prev.filter((x) => x.key !== b.key))}
                  />
                </div>
              )}
            </div>
          </DialogField>
        ))}

        {!locked && (
          <div>
            <Button variant="secondary" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setBlocks((prev) => [...prev, ...toBlocks([''])])}>
              Add text block
            </Button>
          </div>
        )}
      </div>
    </AdminFormModal>
  );
}
